import { shouldHideDraftProducts } from '@/config/deploy';
import { shopifyFetch } from './shopifyClient';
import type { ShopifyCart, ShopifyProduct, ShopifyProductCollection, ShopifyProductImage } from '@/types/shopify';

/** Namespace / clé du booléen « produit mis en avant » (admin Shopify → métachamps). */
const PRODUCT_FEATURED_METAFIELD = { namespace: `custom`, key: `is_featured` } as const;
/** Namespace / clé du booléen « brouillon / préprod » (visible uniquement hors prod). */
const PRODUCT_DRAFT_METAFIELD = { namespace: `custom`, key: `isDraft` } as const;

const PRODUCT_METAFIELDS_GRAPHQL = `
  featuredMetafield: metafield(
    namespace: "${PRODUCT_FEATURED_METAFIELD.namespace}",
    key: "${PRODUCT_FEATURED_METAFIELD.key}"
  ) {
    value
  }
  draftMetafield: metafield(
    namespace: "${PRODUCT_DRAFT_METAFIELD.namespace}",
    key: "${PRODUCT_DRAFT_METAFIELD.key}"
  ) {
    value
  }
`;

function parseShopifyBooleanMetafield(value: string | null | undefined): boolean {
  if (value == null || value === ``) return false;
  const v = String(value).trim().toLowerCase();
  return (
    v === `true` ||
    v === `1` ||
    v === `yes` ||
    v === `oui` ||
    v === `vrai`
  );
}

function isProductVisibleOnSite(product: ShopifyProduct): boolean {
  if (!shouldHideDraftProducts()) return true;
  return !product.isDraft;
}

function normalizeShopifyImageUrl(url: string): string {
  try {
    const { origin, pathname } = new URL(url);
    return `${origin}${pathname}`;
  } catch {
    return url.split(`?`)[0] ?? url;
  }
}

function dedupeProductImages(images: ShopifyProductImage[]): ShopifyProductImage[] {
  const seen = new Set<string>();
  return images.filter((img) => {
    if (!img?.url) return false;
    const key = normalizeShopifyImageUrl(img.url);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function collectProductGalleryImages(raw: any): ShopifyProductImage[] {
  const imageNodes =
    (raw.images?.edges ?? []).map((edge: { node: ShopifyProductImage }) => edge.node) ?? [];

  const mediaImageNodes = (raw.media?.nodes ?? [])
    .map((mediaNode: { image?: ShopifyProductImage }) => mediaNode?.image)
    .filter(
      (img: ShopifyProductImage | undefined): img is ShopifyProductImage =>
        Boolean(img),
    );

  // `media` = galerie « Supports multimédias » ; `images` = ancienne API (souvent identique).
  const source = mediaImageNodes.length > 0 ? mediaImageNodes : imageNodes;
  return dedupeProductImages(source);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapShopifyProductNode(raw: any): ShopifyProduct {
  const images = collectProductGalleryImages(raw);

  const collections: ShopifyProductCollection[] = (raw.collections?.nodes ?? []).map(
    (col: { handle: string; title: string }) => ({
      handle: col.handle,
      title: col.title,
    }),
  );

  return {
    id: raw.id,
    handle: raw.handle,
    title: raw.title,
    description: raw.description ?? null,
    descriptionHtml: raw.descriptionHtml ?? null,
    featuredImage: raw.featuredImage ?? null,
    images,
    options: raw.options ?? [],
    priceRange: raw.priceRange,
    collections,
    isFeatured: parseShopifyBooleanMetafield(raw.featuredMetafield?.value),
    isDraft: parseShopifyBooleanMetafield(raw.draftMetafield?.value),
    variants:
      (raw.variants?.edges ?? []).map(
        (edge: { node: { price: { amount: string } } & Record<string, unknown> }) => ({
          ...edge.node,
          price: edge.node.price.amount,
        }),
      ) ?? [],
  };
}

interface ProductsResponse {
  products: {
    edges: {
      node: ShopifyProduct;
    }[];
  };
}

/** Champs minimaux pour les cartes produit en liste (pagination). */
const PRODUCT_LIST_NODE_GRAPHQL = `
  id
  handle
  title
  description
  featuredImage {
    id
    url
    altText
  }
  images(first: 1) {
    edges {
      node {
        id
        url
        altText
      }
    }
  }
  priceRange {
    minVariantPrice {
      amount
      currencyCode
    }
    maxVariantPrice {
      amount
      currencyCode
    }
  }
  variants(first: 1) {
    edges {
      node {
        id
        price: priceV2 {
          amount
          currencyCode
        }
      }
    }
  }
  collections(first: 10) {
    nodes {
      handle
      title
    }
  }
  ${PRODUCT_METAFIELDS_GRAPHQL}
`;

interface ProductsPageResponse {
  products: {
    edges: {
      node: unknown;
    }[];
    pageInfo: {
      hasNextPage: boolean;
      endCursor: string | null;
    };
  };
}

export interface ProductsPageInfo {
  hasNextPage: boolean;
  endCursor: string | null;
}

export interface ProductsPageResult {
  products: ShopifyProduct[];
  pageInfo: ProductsPageInfo;
}

export const PRODUCTS_LIST_PAGE_SIZE = 12;

const MAX_PAGES_PER_VISIBLE_BATCH = 5;

interface ProductByHandleResponse {
  productByHandle: ShopifyProduct | null;
}

interface CartResponse {
  cart: ShopifyCart | null;
}

interface CartCreateResponse {
  cartCreate: {
    cart: ShopifyCart | null;
  };
}

interface CartLinesAddResponse {
  cartLinesAdd: {
    cart: ShopifyCart | null;
  };
}

interface CartLinesUpdateResponse {
  cartLinesUpdate: {
    cart: ShopifyCart | null;
  };
}

interface CartLinesRemoveResponse {
  cartLinesRemove: {
    cart: ShopifyCart | null;
  };
}

interface CartDiscountCodesUpdateResponse {
  cartDiscountCodesUpdate: {
    cart: ShopifyCart | null;
  };
}

export async function getProductsPage({
  first = PRODUCTS_LIST_PAGE_SIZE,
  after = null,
}: {
  first?: number;
  after?: string | null;
}): Promise<ProductsPageResult> {
  const data = await shopifyFetch<ProductsPageResponse>({
    query: `
      query GetProductsPage($first: Int!, $after: String) {
        products(first: $first, after: $after) {
          pageInfo {
            hasNextPage
            endCursor
          }
          edges {
            node {
              ${PRODUCT_LIST_NODE_GRAPHQL}
            }
          }
        }
      }
    `,
    variables: {
      first,
      after: after ?? undefined,
    },
  });

  return {
    products: data.products.edges.map(({ node }) => mapShopifyProductNode(node)),
    pageInfo: data.products.pageInfo,
  };
}

/** Charge assez de produits visibles (hors brouillons) pour remplir une « page » UI. */
export async function fetchVisibleProductsPage(
  targetCount: number = PRODUCTS_LIST_PAGE_SIZE,
  after: string | null = null,
): Promise<ProductsPageResult> {
  const visible: ShopifyProduct[] = [];
  let cursor = after;
  let hasNextPage = true;
  let pagesFetched = 0;

  while (
    hasNextPage &&
    pagesFetched < MAX_PAGES_PER_VISIBLE_BATCH &&
    (visible.length < targetCount ||
      (visible.length === 0 && pagesFetched === 0))
  ) {
    const page = await getProductsPage({
      first: targetCount,
      after: cursor,
    });

    for (const product of page.products) {
      if (isProductVisibleOnSite(product)) {
        visible.push(product);
      }
    }

    cursor = page.pageInfo.endCursor;
    hasNextPage = page.pageInfo.hasNextPage;
    pagesFetched += 1;

    if (page.products.length === 0) {
      break;
    }
  }

  return {
    products: visible,
    pageInfo: {
      hasNextPage,
      endCursor: cursor,
    },
  };
}

export async function getProducts(): Promise<ShopifyProduct[]> {
  const data = await shopifyFetch<ProductsResponse>({
    query: `
      query GetProducts {
        products(first: 100) {
          edges {
            node {
              id
              handle
              title
              description
              featuredImage {
                id
                url
                altText
              }
              media(first: 10) {
                nodes {
                  ... on MediaImage {
                    image {
                      id
                      url
                      altText
                    }
                  }
                }
              }
              images(first: 10) {
                edges {
                  node {
                    id
                    url
                    altText
                  }
                }
              }
              options {
                id
                name
                values
              }
              variants(first: 50) {
                edges {
                  node {
                    id
                    title
                    image {
                      id
                      url
                      altText
                    }
                    price: priceV2 {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                    quantityAvailable
                    availableForSale
                  }
                }
              }
              priceRange {
                minVariantPrice {
                  amount
                  currencyCode
                }
              }
              collections(first: 10) {
                nodes {
                  handle
                  title
                }
              }
              ${PRODUCT_METAFIELDS_GRAPHQL}
            }
          }
        }
      }
    `,
  });

  return data.products.edges
    .map(({ node }) => mapShopifyProductNode(node))
    .filter(isProductVisibleOnSite);
}

export async function getProductByHandle(handle: string): Promise<ShopifyProduct | null> {
  const data = await shopifyFetch<ProductByHandleResponse>({
    query: `
      query GetProductByHandle($handle: String!) {
        productByHandle(handle: $handle) {
          id
          handle
          title
          description
          descriptionHtml
          featuredImage {
            id
            url
            altText
          }
          media(first: 10) {
            nodes {
              ... on MediaImage {
                image {
                  id
                  url
                  altText
                }
              }
            }
          }
          images(first: 10) {
            edges {
              node {
                id
                url
                altText
              }
            }
          }
          options {
            id
            name
            values
          }
          variants(first: 50) {
            edges {
              node {
                id
                title
                image {
                  id
                  url
                  altText
                }
                price: priceV2 {
                  amount
                  currencyCode
                }
                selectedOptions {
                  name
                  value
                }
                quantityAvailable
                availableForSale
              }
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
          }
          collections(first: 10) {
            nodes {
              handle
              title
            }
          }
          ${PRODUCT_METAFIELDS_GRAPHQL}
        }
      }
    `,
    variables: { handle },
  });

  const product = data.productByHandle;
  if (!product) return null;

  const mapped = mapShopifyProductNode(product);
  if (!isProductVisibleOnSite(mapped)) return null;

  return mapped;
}

export async function cartGet(cartId: string): Promise<ShopifyCart | null> {
  const data = await shopifyFetch<CartResponse>({
    query: `
      query GetCart($id: ID!) {
        cart(id: $id) {
          id
          checkoutUrl
          totalQuantity
          discountCodes {
            code
            applicable
          }
          cost {
            subtotalAmount {
              amount
              currencyCode
            }
            totalAmount {
              amount
              currencyCode
            }
          }
          lines(first: 100) {
            edges {
              node {
                id
                quantity
                cost {
                  amountPerQuantity {
                    amount
                    currencyCode
                  }
                  subtotalAmount {
                    amount
                    currencyCode
                  }
                  totalAmount {
                    amount
                    currencyCode
                  }
                }
                discountAllocations {
                  discountedAmount {
                    amount
                    currencyCode
                  }
                  ... on CartCodeDiscountAllocation {
                    code
                  }
                }
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    image {
                      id
                      url
                      altText
                    }
                    product {
                      id
                      title
                      handle
                      featuredImage {
                        id
                        url
                        altText
                      }
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: { id: cartId },
  });

  return data.cart ?? null;
}

export async function cartCreate(): Promise<ShopifyCart | null> {
  const data = await shopifyFetch<CartCreateResponse>({
    query: `
      mutation CartCreate {
        cartCreate(input: {}) {
          cart {
            id
            checkoutUrl
            totalQuantity
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  cost {
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                    }
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        id
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          id
                          url
                          altText
                        }
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
  });

  return data.cartCreate.cart ?? null;
}

export async function cartLinesAdd(cartId: string, lines: { merchandiseId: string; quantity: number }[]) {
  const data = await shopifyFetch<CartLinesAddResponse>({
    query: `
      mutation CartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
            totalQuantity
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  cost {
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                    }
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        id
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          id
                          url
                          altText
                        }
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      cartId,
      lines,
    },
  });

  return data.cartLinesAdd.cart ?? null;
}

export async function cartLinesUpdate(
  cartId: string,
  lines: { id: string; merchandiseId: string; quantity: number }[]
) {
  const data = await shopifyFetch<CartLinesUpdateResponse>({
    query: `
      mutation CartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
        cartLinesUpdate(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
            totalQuantity
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  cost {
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                    }
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        id
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          id
                          url
                          altText
                        }
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      cartId,
      lines,
    },
  });

  return data.cartLinesUpdate.cart ?? null;
}

export async function cartLinesRemove(cartId: string, lineIds: string[]) {
  const data = await shopifyFetch<CartLinesRemoveResponse>({
    query: `
      mutation CartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            checkoutUrl
            totalQuantity
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  cost {
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                    }
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        id
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          id
                          url
                          altText
                        }
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    `,
    variables: {
      cartId,
      lineIds,
    },
  });

  return data.cartLinesRemove.cart ?? null;
}

export async function cartDiscountCodesUpdate(cartId: string, discountCodes: string[]) {
  const data = await shopifyFetch<CartDiscountCodesUpdateResponse>({
    query: `
      mutation cartDiscountCodesUpdate($cartId: ID!, $discountCodes: [String!]!) {
        cartDiscountCodesUpdate(cartId: $cartId, discountCodes: $discountCodes) {
          cart {
            id
            checkoutUrl
            totalQuantity
            discountCodes {
              code
              applicable
            }
            cost {
              subtotalAmount {
                amount
                currencyCode
              }
              totalAmount {
                amount
                currencyCode
              }
            }
            lines(first: 100) {
              edges {
                node {
                  id
                  quantity
                  cost {
                    amountPerQuantity {
                      amount
                      currencyCode
                    }
                    subtotalAmount {
                      amount
                      currencyCode
                    }
                    totalAmount {
                      amount
                      currencyCode
                    }
                  }
                  discountAllocations {
                    discountedAmount {
                      amount
                      currencyCode
                    }
                    ... on CartCodeDiscountAllocation {
                      code
                    }
                  }
                  merchandise {
                    ... on ProductVariant {
                      id
                      title
                      image {
                        id
                        url
                        altText
                      }
                      product {
                        id
                        title
                        handle
                        featuredImage {
                          id
                          url
                          altText
                        }
                      }
                      selectedOptions {
                        name
                        value
                      }
                    }
                  }
                }
              }
            }
          }
          userErrors {
            field
            message
          }
        }
      }
    `,
    variables: {
      cartId,
      discountCodes,
    },
  });

  return data.cartDiscountCodesUpdate.cart ?? null;
}

